import { useMemo, useState } from 'react';

export interface CancelDialogBag {
  showDialog: boolean;
  handleDiscardAndContinue: () => void;
  handleSaveAndContinue: () => void;
  handleCancel: () => void;
  wrapAction: (action: ArgAction) => ArgAction;
  saving: boolean;
}

interface EmptyAction {
  (): void;
}

interface ArgAction {
  (...args: any): void;
}

export function useCancelDialog(
  isFormDirty: boolean,
  saveChanges: () => Promise<unknown>
): CancelDialogBag {
  const [action, setAction] = useState<EmptyAction>();

  const wrapAction =
    (injectedAction: ArgAction): ArgAction =>
    (...args: any[]) => {
      if (isFormDirty) {
        setAction(() => () => injectedAction(...args));
      } else {
        injectedAction(...args);
      }
    };

  const handleConfirm = () => {
    action && action();
    setAction(undefined);
  };

  const handleCancel = () => setAction(undefined);
  const [saving, setSaving] = useState(false);
  const handleSave = () => {
    setSaving(true);
    return saveChanges()
      .then(() => {
        setSaving(false);
        handleConfirm();
      })
      .catch(() => setSaving(false));
  };

  return useMemo(
    (): CancelDialogBag => ({
      showDialog: !!action,
      handleDiscardAndContinue: handleConfirm,
      handleCancel,
      wrapAction,
      handleSaveAndContinue: handleSave,
      saving,
    }),
    [action, isFormDirty, saveChanges]
  );
}
